import type { AvailabilityConfidence, CartSnapshot } from '@teo/widget-shared';
import { confidenceRank, type CartPartial } from './cart-partial.js';

function pickField<T>(
  partials: CartPartial[],
  read: (partial: CartPartial) => T | undefined,
  /** Prefer higher confidence; break ties with earlier (higher-priority) partials. */
): { value: T; confidence: AvailabilityConfidence; source: string } | undefined {
  let best:
    | { value: T; confidence: AvailabilityConfidence; source: string; index: number }
    | undefined;

  partials.forEach((partial, index) => {
    const value = read(partial);
    if (value === undefined) return;
    const rank = confidenceRank(partial.confidence);
    if (
      !best ||
      rank > confidenceRank(best.confidence) ||
      (rank === confidenceRank(best.confidence) && index < best.index)
    ) {
      best = {
        value,
        confidence: partial.confidence,
        source: partial.source,
        index,
      };
    }
  });

  return best
    ? { value: best.value, confidence: best.confidence, source: best.source }
    : undefined;
}

/**
 * Merge cart partials. Order matters for equal-confidence ties:
 * pass high-trust sources first (GTM, Shopify, cart DOM, then header badge).
 *
 * Header badge never supplies value/currency (collectors enforce that).
 */
export function resolveCartSnapshot(
  partials: CartPartial[],
  collectedAt: string = new Date().toISOString(),
): CartSnapshot | null {
  const usable = partials.filter(Boolean);
  if (usable.length === 0) return null;

  // Value / currency: ignore low-confidence partials entirely.
  const valueEligible = usable.filter((p) => p.confidence !== 'low');

  const countPick = pickField(usable, (p) => p.itemsCount);
  const valuePick = pickField(valueEligible, (p) => p.cartValue);
  const currencyPick = pickField(valueEligible, (p) => p.currency);

  if (!countPick && !valuePick && !currencyPick) {
    const empty = usable.find((p) => p.emptyExplicit);
    if (!empty) return null;
    return {
      itemsCount: 0,
      cartValue: 0,
      confidence: empty.confidence,
      sources: [empty.source],
      collectedAt,
    };
  }

  const contributing = new Set<string>();
  const confidences: AvailabilityConfidence[] = [];

  if (countPick) {
    contributing.add(countPick.source);
    confidences.push(countPick.confidence);
  }
  if (valuePick) {
    contributing.add(valuePick.source);
    confidences.push(valuePick.confidence);
  }
  if (currencyPick) {
    contributing.add(currencyPick.source);
    confidences.push(currencyPick.confidence);
  }

  let confidence: AvailabilityConfidence = 'low';
  for (const c of confidences) {
    if (confidenceRank(c) > confidenceRank(confidence)) confidence = c;
  }

  return {
    ...(countPick ? { itemsCount: countPick.value } : {}),
    ...(valuePick ? { cartValue: valuePick.value } : {}),
    ...(currencyPick ? { currency: currencyPick.value } : {}),
    confidence,
    sources: [...contributing],
    collectedAt,
  };
}
